import { create } from 'zustand'
import { temporal } from 'zundo'
import type { Block } from '@/domain/model/Block'
import type { BlockType, BlockProps, BlockSettings } from '@brix/types'

export type PreviewDevice = 'desktop' | 'tablet' | 'mobile'

interface EditorState {
  pageId: string | null
  blocks: Block[]
  selectedBlockId: string | null
  isDirty: boolean
  isSaving: boolean
  previewMode: boolean
  previewDevice: PreviewDevice

  loadPage: (pageId: string, blocks: Block[]) => void
  selectBlock: (id: string | null) => void
  addBlock: (type: BlockType, defaultProps?: Partial<BlockProps>, afterId?: string) => void
  updateBlockProps: (id: string, props: Partial<BlockProps>) => void
  updateBlockSettings: (id: string, settings: Partial<BlockSettings>) => void
  deleteBlock: (id: string) => void
  reorderBlocks: (activeId: string, overId: string) => void
  setBlockOrder: (orderedIds: string[]) => void
  setIsSaving: (v: boolean) => void
  setIsDirty: (v: boolean) => void
  togglePreview: () => void
  setPreviewDevice: (device: PreviewDevice) => void
  reset: () => void
}

const defaultPropsForType = (type: BlockType): BlockProps => {
  // Cast needed because TypeScript can't narrow the union for each key in the literal
  const defaults = {
    hero: { headline: 'Your Headline Here', alignment: 'center', layout: 'full-height' },
    text: { content: '<p>Start writing...</p>', alignment: 'left' },
    image: { src: '', alt: '', objectFit: 'cover', aspectRatio: '16/9' },
    gallery: { images: [], columns: 3, gap: 'md', lightbox: true },
    video: { src: '', controls: true },
    cta: { headline: 'Ready to get started?', primaryText: 'Get started', primaryUrl: '#', alignment: 'center' },
    features: { items: [], columns: 3, layout: 'card' },
    pricing: { tiers: [] },
    testimonials: { items: [], layout: 'grid' },
    'contact-form': {
      fields: [
        { name: 'name', label: 'Name', type: 'text', required: true },
        { name: 'email', label: 'Email', type: 'email', required: true },
        { name: 'message', label: 'Message', type: 'textarea', required: true },
      ],
      submitText: 'Send message',
      successMessage: 'Thanks! We will be in touch soon.',
      recipientEmail: '',
    },
    'blog-feed': { postsCount: 6, layout: 'grid', showExcerpt: true, showCover: true },
    grid: { columns: 3, gap: 'md', responsive: true, items: [{content:''},{content:''},{content:''}] },
    columns: { widths: [50, 50], gap: 'md', items: [{content:''},{content:''}] },
    spacer: { height: 64 },
    divider: {},
    embed: { code: '' },
    html: { code: '' },
    logos: { logos: [] },
    stats: { items: [] },
    quote: { quote: '' },
    cards: { items: [] },
    'product-catalog': { items: [] },
  } as Record<BlockType, BlockProps>
  return defaults[type] ?? ({} as BlockProps)
}

export const useEditorStore = create<EditorState>()(
  temporal(
    (set, get) => ({
      pageId: null,
      blocks: [],
      selectedBlockId: null,
      isDirty: false,
      isSaving: false,
      previewMode: false,
      previewDevice: 'desktop' as PreviewDevice,

      loadPage: (pageId, blocks) => set({ pageId, blocks, isDirty: false, selectedBlockId: null }),

      selectBlock: (id) => set({ selectedBlockId: id }),

      addBlock: (type, overrideProps, afterId) => {
        const blocks = get().blocks
        const afterIndex = afterId ? blocks.findIndex((b) => b.id === afterId) : blocks.length - 1
        const insertAt = afterIndex + 1

        const newBlock = {
          id: crypto.randomUUID(),
          pageId: get().pageId ?? '',
          type,
          props: { ...defaultPropsForType(type), ...overrideProps } as BlockProps,
          settings: {},
          orderIndex: insertAt,
          parentId: null,
          children: [],
          createdAt: new Date(),
          updatedAt: new Date(),
        }

        const updatedBlocks = [
          ...blocks.slice(0, insertAt),
          newBlock as unknown as Block,
          ...blocks.slice(insertAt).map((b, i) => ({ ...b, orderIndex: insertAt + 1 + i }) as Block),
        ]

        set({ blocks: updatedBlocks, isDirty: true, selectedBlockId: newBlock.id })
      },

      updateBlockProps: (id, props) => {
        set((state) => ({
          blocks: state.blocks.map((b) =>
            b.id === id ? ({ ...b, props: { ...b.props, ...props }, updatedAt: new Date() } as Block) : b,
          ),
          isDirty: true,
        }))
      },

      updateBlockSettings: (id, settings) => {
        set((state) => ({
          blocks: state.blocks.map((b) =>
            b.id === id ? ({ ...b, settings: { ...b.settings, ...settings }, updatedAt: new Date() } as Block) : b,
          ),
          isDirty: true,
        }))
      },

      deleteBlock: (id) => {
        set((state) => ({
          blocks: state.blocks
            .filter((b) => b.id !== id)
            .map((b, i) => ({ ...b, orderIndex: i }) as Block),
          selectedBlockId: state.selectedBlockId === id ? null : state.selectedBlockId,
          isDirty: true,
        }))
      },

      reorderBlocks: (activeId, overId) => {
        const blocks = get().blocks
        const oldIndex = blocks.findIndex((b) => b.id === activeId)
        const newIndex = blocks.findIndex((b) => b.id === overId)
        if (oldIndex === -1 || newIndex === -1) return

        const updated = [...blocks]
        const [moved] = updated.splice(oldIndex, 1)
        updated.splice(newIndex, 0, moved!)
        const reordered = updated.map((b, i) => ({ ...b, orderIndex: i }) as Block)
        set({ blocks: reordered, isDirty: true })
      },

      // Applies a full target order (e.g. from the AI assistant) rather than
      // a single pairwise drag swap. Any block id not present in `orderedIds`
      // keeps its relative position, appended after the ones that were
      // reordered — defensive against an incomplete list rather than
      // silently dropping blocks.
      setBlockOrder: (orderedIds) => {
        const blocks = get().blocks
        const byId = new Map(blocks.map((b) => [b.id, b]))
        const ordered = orderedIds.map((id) => byId.get(id)).filter((b): b is Block => !!b)
        const orderedIdSet = new Set(ordered.map((b) => b.id))
        const remaining = blocks.filter((b) => !orderedIdSet.has(b.id))
        const merged = [...ordered, ...remaining].map((b, i) => ({ ...b, orderIndex: i }) as Block)
        set({ blocks: merged, isDirty: true })
      },

      setIsSaving: (v) => set({ isSaving: v }),
      setIsDirty: (v) => set({ isDirty: v }),
      togglePreview: () => set((s) => ({ previewMode: !s.previewMode, previewDevice: 'desktop' as PreviewDevice })),
      setPreviewDevice: (device) => set({ previewDevice: device }),
      reset: () => set({ pageId: null, blocks: [], selectedBlockId: null, isDirty: false }),
    }),
    { limit: 50 },
  ),
)
